import { useState, useMemo, useRef, useEffect } from 'react';
import ListGroup from 'react-bootstrap/ListGroup';
import Button from 'react-bootstrap/Button';
import Pagination from 'react-bootstrap/Pagination';
import './CourseSeries.css';

const CourseSeries = ({
  title,
  data,
  headerStyle = 'right-bar', // 'first-bar', 'right-bar', or 'purple-bar'
  buttonStyle = 'YlectureButton', // 'XlectureButton', 'YlectureButton', 'QuantButton', or 'button-3'
  containerStyle = {},
  itemsPerPage = 20,
  showPagination = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const componentRef = useRef(null);

  // Calculate pagination
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = useMemo(() => data.slice(startIndex, endIndex), [data, startIndex, endIndex]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of component when page changes
    if (componentRef.current) {
      componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  return (
    <div ref={componentRef} className="course-series-container">
      <ListGroup style={{ marginTop: '2rem', ...containerStyle }} className="course-series-list">
        <ListGroup.Item 
          id={headerStyle} 
          active={false} 
          action={false}
          className="series-header-item"
          style={{
            padding: '1.5rem',
            fontSize: '1.3rem',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{title}</span>
          {showPagination && totalPages > 1 && (
            <span className="pagination-info" style={{ fontSize: '0.95rem', opacity: 0.8, fontWeight: 'normal' }}>
              Page {currentPage} of {totalPages}
            </span>
          )}
        </ListGroup.Item>
      {currentData.map((lecture, index) => {
        return (
          <ListGroup.Item 
            key={lecture.title} 
            active={false} 
            action={false}
            className="lecture-item"
            style={{
              padding: '1.25rem',
              borderLeft: '1px solid #e0e0e0',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderLeftColor = '#667eea';
              e.currentTarget.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderLeftColor = '#e0e0e0';
                e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div className="lecture-content">
              <div className="lecture-header">
                <h5 className="lecture-title">{lecture.title}</h5>
                <span className="lecture-number">#{startIndex + index + 1}</span>
              </div>
              <div className="lecture-links" style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {lecture.links.map((link) => {
                  return (
                    <Button
                      key={link.link}
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      id={buttonStyle}
                      className="lecture-link-button"
                      size="sm"
                      variant="outline-dark"
                      style={{
                        marginRight: '0.5rem',
                        marginBottom: '0.5rem',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                      }}
                    >
                      <span>{link.text}</span>
                      {link.isYoutube ? (
                        <img
                          src={require('../../assets/youtube.png')}
                          width="16"
                          height="16"
                          alt="YouTube"
                          style={{ marginLeft: '0.25rem' }}
                        />
                      ) : (
                        <img
                          src={require('../../assets/docu.png')}
                          width="16"
                          height="16"
                          alt="Document"
                          style={{ marginLeft: '0.25rem' }}
                        />
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          </ListGroup.Item>
        );
      })}
      {showPagination && totalPages > 1 && (
        <ListGroup.Item>
          <div className="d-flex justify-content-center">
            <Pagination>
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  // Show first, last, current, and pages around current
                  return (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  );
                })
                .map((page, index, array) => {
                  // Add ellipsis if there's a gap
                  const showEllipsisBefore = index > 0 && array[index - 1] < page - 1;
                  return (
                    <span key={page}>
                      {showEllipsisBefore && <Pagination.Ellipsis disabled />}
                      <Pagination.Item
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    </span>
                  );
                })}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </ListGroup.Item>
      )}
      </ListGroup>
    </div>
  );
};

export default CourseSeries;

